import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type BuyerParticipationRequestDetailDto,
  type BuyerBidV2EspdDto,
  useSetQualificationVerdictMutation,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../../common/formatting/useDisplayDateWithTime";
import { QualificationVerdictControl } from "../../bid-detail/QualificationVerdictControl";
import { formatDecidedBy } from "./formatDecidedBy";
import { getRequestQualificationProgress } from "./requestQualification";
import { useBuyerReviewNotesEditor } from "./useBuyerReviewNotesEditor";
import { SaveStatusBadge } from "./SaveStatusBadge";

interface Props {
  eventId: string;
  detail: BuyerParticipationRequestDetailDto;
  optimisticReviewed?: number;
  onShowEspd?: () => void;
}

// Counts criteria that have answer !== OK (deviations + unanswered).
const deriveEspdDigest = (espd: BuyerBidV2EspdDto): { flagged: number; total: number } => {
  const flagged = (espd.deviationCount ?? 0) + (espd.unansweredCount ?? 0);
  const total = (espd.okCount ?? 0) + flagged;
  return { flagged, total };
};

export const QualificationDecisionCard = ({ eventId, detail, optimisticReviewed, onShowEspd }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const apiError = useApiError();
  const [setVerdict, { isLoading: isVerdictLoading }] = useSetQualificationVerdictMutation();
  const [rejectionPanelOpen, setRejectionPanelOpen] = useState(false);

  const request = detail.request;
  const verdict = request.qualificationVerdict ?? null;
  const locked = detail.invitationConfirmed ?? false;
  // The BFF always returns an ESPD DTO; hasResponse=false means no ESPD was submitted.
  const espdDigest = detail.espd?.hasResponse ? deriveEspdDigest(detail.espd) : null;

  const editor = useBuyerReviewNotesEditor(eventId, request.id, {
    internalNote: request.internalNote,
    rejectionReason: request.rejectionReason,
  });

  const progress = getRequestQualificationProgress(
    detail.selectionCriteria ?? [],
    request.qualificationResponses ?? [],
  );

  // The "all reviewed" gate respects an optimistic count so the parent can unblock
  // the Qualified button before the refetch settles.
  const effectiveReviewed = optimisticReviewed ?? progress.reviewed;
  const allReviewed = !progress.hasRequirements || effectiveReviewed >= progress.total;
  const pendingCount = progress.hasRequirements ? progress.total - effectiveReviewed : 0;

  // Rejection panel is visible when:
  // - committed rejection verdict (keeps the reason editable), OR
  // - user explicitly opened it by clicking "Not qualified" and no qualified verdict exists.
  // A non-blank leftover draft alone does NOT force the panel open when the verdict is
  // null or qualified — that would re-open the panel after an undo.
  const rejectionPanelVisible = verdict?.qualified === false || rejectionPanelOpen;

  const submitVerdict = async (qualified: boolean | null) => {
    // Flush any pending debounced note save before changing the verdict so a
    // keystroke still in the debounce window does not land after the mutation.
    await editor.flush();
    try {
      await setVerdict({
        eventId,
        requestId: request.id,
        setQualificationVerdictRequestDto: { qualified },
      }).unwrap();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const handleVerdictChange = async (value: boolean | null) => {
    if (value === false) {
      // Expand the rejection panel without committing.
      setRejectionPanelOpen(true);
      return;
    }
    if (value === null) {
      // User toggled off an existing verdict — collapse rejection panel.
      setRejectionPanelOpen(false);
    }
    await submitVerdict(value);
  };

  const handleSetNotQualified = async () => {
    // This path bypasses submitVerdict (to carry the trimmed reason in the verdict
    // payload), so it flushes the pending note save itself before committing.
    await editor.flush();
    try {
      await setVerdict({
        eventId,
        requestId: request.id,
        setQualificationVerdictRequestDto: {
          qualified: false,
          rejectionReason: editor.values.rejectionReason.trim(),
        },
      }).unwrap();
      setRejectionPanelOpen(false);
    } catch (e) {
      apiError.trigger(e);
    }
  };

  // Locked view: read-only rendering with no buttons or textareas.
  if (locked) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody>
          <Stack spacing="4">
            <Box>
              <Text fontWeight="semibold" color="smText" mb="1">
                {t("Qualification decision")}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {t("The invitation is confirmed — the decision is final.")}
              </Text>
            </Box>

            {espdDigest && (
              <Text fontSize="sm" color="smMuted">
                {espdDigest.flagged > 0
                  ? t("ESPD: {{flagged}} of {{total}} declarations need attention — see the ESPD tab", {
                      flagged: espdDigest.flagged,
                      total: espdDigest.total,
                    })
                  : t("ESPD: no declarations need attention")}
              </Text>
            )}

            {request.internalNote && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
                  {t("Internal note")}
                </Text>
                <Text fontSize="sm" color="smText">
                  {request.internalNote}
                </Text>
              </Box>
            )}

            {verdict && (
              <Box>
                <HStack mb="1">
                  <Badge colorScheme={verdict.qualified ? "green" : "red"}>
                    {verdict.qualified ? t("Qualified") : t("Not qualified")}
                  </Badge>
                </HStack>
                {request.rejectionReason && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
                      {t("Reason for rejection")}
                    </Text>
                    <Text fontSize="sm" color="smText">
                      {request.rejectionReason}
                    </Text>
                  </Box>
                )}
                <Text fontSize="sm" color="smMuted">
                  {formatDecidedBy(verdict, displayDateWithTime, t)}
                </Text>
              </Box>
            )}
          </Stack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
      <CardBody>
        <Stack spacing="5">
          <Text fontWeight="semibold" color="smText">
            {t("Qualification decision")}
          </Text>

          {/* ESPD digest — informational only, never gates the verdict */}
          {espdDigest &&
            (espdDigest.flagged > 0 ? (
              <Alert status="info" rounded="md" fontSize="sm" py="2">
                <AlertIcon />
                <Button
                  variant="link"
                  fontSize="sm"
                  color="inherit"
                  onClick={onShowEspd}
                  fontWeight="normal"
                >
                  {t("ESPD: {{flagged}} of {{total}} declarations need attention — see the ESPD tab", {
                    flagged: espdDigest.flagged,
                    total: espdDigest.total,
                  })}
                </Button>
              </Alert>
            ) : (
              <Text fontSize="sm" color="smMuted">
                {t("ESPD: no declarations need attention")}
              </Text>
            ))}

          {/* Internal note */}
          <Box>
            <HStack mb="1" align="center">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Internal note")}
              </Text>
              <SaveStatusBadge status={editor.statusFor("internalNote")} />
            </HStack>
            <Textarea
              aria-label={t("Internal note") ?? undefined}
              value={editor.values.internalNote}
              onChange={(e) => editor.setField("internalNote", e.target.value)}
              bg="smBackgroundSecondary"
              rounded="md"
              fontSize="sm"
              mb="1"
            />
            <Text fontSize="xs" color="smMuted">
              {t("Only visible to the buying team. Never shared with the supplier.")}
            </Text>
          </Box>

          {/* Verdict toggle */}
          <Box>
            <QualificationVerdictControl
              value={verdict?.qualified ?? null}
              onChange={handleVerdictChange}
              isReadOnly={isVerdictLoading}
              isPositiveDisabled={!allReviewed && verdict?.qualified !== true}
              positiveLabel={t("Qualified")}
              negativeLabel={t("Not qualified")}
            />

            {!allReviewed && verdict?.qualified !== true && (
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Review all qualification requirements first ({{count}} remaining)", {
                  count: pendingCount,
                })}
              </Text>
            )}

            {verdict && (
              <Box mt="2" fontSize="sm" color="smMuted">
                <Text>
                  {formatDecidedBy(verdict, displayDateWithTime, t)}
                </Text>
                <Text fontSize="xs">{t("Click the active choice to undo the decision")}</Text>
              </Box>
            )}
          </Box>

          {/* Rejection panel */}
          {rejectionPanelVisible && (
            <Box>
              <HStack mb="1" align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="medium" color="smText">
                  {t("Reason for rejection")}
                  <Box as="span" color="smError" ml="0.5">
                    *
                  </Box>
                </Text>
                <SaveStatusBadge status={editor.statusFor("rejectionReason")} />
                {editor.values.rejectionReason.trim().length > 0 && verdict?.qualified !== false && (
                  <Badge colorScheme="orange" fontWeight="normal">
                    {t("Draft — the decision is not registered yet")}
                  </Badge>
                )}
              </HStack>
              <Textarea
                aria-label={t("Reason for rejection") ?? undefined}
                value={editor.values.rejectionReason}
                onChange={(e) => editor.setField("rejectionReason", e.target.value)}
                bg="smBackgroundSecondary"
                rounded="md"
                fontSize="sm"
                mb="1"
              />
              <Text fontSize="xs" color="smMuted" mb="3">
                {t("Sent to the supplier with the rejection when the invitation is confirmed.")}
              </Text>
              {verdict?.qualified !== false && (
                <Button
                  colorScheme="red"
                  size="sm"
                  isDisabled={editor.values.rejectionReason.trim().length === 0 || isVerdictLoading}
                  isLoading={isVerdictLoading}
                  onClick={handleSetNotQualified}
                >
                  {t("Set as not qualified")}
                </Button>
              )}
            </Box>
          )}

          {/* Retracted-review warning — only for qualified verdicts; for rejections we show
              the "(N remaining)" helper above which is sufficient */}
          {verdict?.qualified === true && progress.hasRequirements && pendingCount > 0 && (
            <Alert status="warning" rounded="md" fontSize="sm" py="2">
              <AlertIcon />
              {t("The review changed after the decision — confirm it again.")}
            </Alert>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
};
